import { FuelConsumptionSummaryItem } from './types';

/**
 * The unit to put on the monthly fuel chart's value axis, or null if there
 * isn't a single honest one.
 *
 * The monthly rows carry only `quantity` — the unit lives on the summary rows —
 * and the factor table genuinely holds more than one (liter, kWh and kg all
 * appear on dev). So a tenant running diesel alongside electric has litres
 * stacked on top of kWh in the same bar, and a fixed "liter" label would state
 * something false about part of every stack.
 *
 * Returns null rather than guessing in that case, so the caller can fall back to
 * a unitless label. Naming no unit is a smaller lie than naming the wrong one.
 */
export function fuelAxisUnit(
  summary: FuelConsumptionSummaryItem[],
  drawnFuelTypes: string[]
): string | null {
  if (summary.length === 0 || drawnFuelTypes.length === 0) return null;

  const unitByFuel = new Map(summary.map((s) => [s.fuelType, s.unit]));

  const units: string[] = [];
  for (const fuelType of drawnFuelTypes) {
    const unit = unitByFuel.get(fuelType);
    // A drawn fuel with no summary row leaves part of the axis undescribed.
    if (unit === undefined) return null;
    if (unit.trim() === '') return null;
    units.push(unit);
  }

  // Compared case- and padding-insensitively so "Liter" and " liter " are one
  // unit, while the label keeps the first row's own spelling.
  const normalised = new Set(units.map((u) => u.trim().toLowerCase()));
  return normalised.size === 1 ? units[0].trim() : null;
}
